import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProjets, deleteProjet } from '../services/api';
import { 
  FolderOpen, 
  Plus, 
  Search, 
  Trash2,
  ExternalLink,
  Clock,
  Play,
  Pause,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const ProjetsPage = () => {
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');

  useEffect(() => {
    loadProjets();
  }, []);

  const loadProjets = async () => {
    try {
      const res = await getProjets();
      setProjets(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nom) => {
    if (!window.confirm(`Supprimer le projet "${nom}" et toutes ses données ?`)) return;
    try {
      await deleteProjet(id);
      toast.success('Projet supprimé');
      loadProjets();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getMargeColor = (pct) => {
    if (pct === null || pct === undefined) return 'text-slate-400';
    if (pct < 15) return 'text-red-600 bg-red-50';
    if (pct < 20) return 'text-orange-600 bg-orange-50';
    return 'text-green-600 bg-green-50';
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'en_cours':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><Play className="w-3 h-3 mr-1" />En cours</span>;
      case 'en_preparation':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />Préparation</span>;
      case 'stand_by':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"><Pause className="w-3 h-3 mr-1" />Stand-by</span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800">{statut}</span>;
    }
  };

  const filteredProjets = projets.filter(p => {
    const matchSearch = p.nom_complet.toLowerCase().includes(search.toLowerCase()) ||
                       p.enseigne_nom.toLowerCase().includes(search.toLowerCase()) ||
                       p.ville.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filterStatut === 'all' || p.statut === filterStatut;
    return matchSearch && matchStatut;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="projets-page">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Projets</h1>
          <p className="text-slate-500">{projets.length} projet(s)</p>
        </div>
        <Link
          to="/projets/nouveau"
          data-testid="new-projet-btn"
          className="flex items-center px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau projet
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un projet..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            data-testid="search-projets"
          />
        </div>
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          data-testid="filter-statut"
        >
          <option value="all">Tous les statuts</option>
          <option value="en_cours">En cours</option>
          <option value="en_preparation">En préparation</option>
          <option value="stand_by">Stand-by</option>
        </select>
      </div>

      {/* Projects Grid */}
      {filteredProjets.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-4">Aucun projet trouvé</p>
          <Link
            to="/projets/nouveau"
            className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Créer un projet
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjets.map((projet) => (
            <div
              key={projet.id}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
              data-testid={`projet-card-${projet.id}`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {projet.enseigne_nom}
                    </p>
                    <h3 className="text-lg font-semibold text-slate-900">{projet.ville}</h3>
                  </div>
                  {getStatutBadge(projet.statut)}
                </div>

                {projet.adresse && (
                  <p className="text-sm text-slate-500 mb-4">{projet.adresse}</p>
                )}

                {/* Financials */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-slate-500">CA MAAG</p>
                    <p className="text-lg font-semibold text-slate-900">{formatMoney(projet.ca_maag)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Marge</p>
                    <p className={`text-lg font-semibold ${getMargeColor(projet.marge_pourcentage).split(' ')[0]}`}>
                      {projet.marge_pourcentage !== null ? `${projet.marge_pourcentage.toFixed(1)}%` : '-'}
                    </p>
                  </div>
                </div>

                {/* Marge bar */}
                <div className="mb-4">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        (projet.marge_pourcentage || 0) >= 20 ? 'bg-green-500' :
                        (projet.marge_pourcentage || 0) >= 15 ? 'bg-orange-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(Math.max(projet.marge_pourcentage || 0, 0), 100)}%` }}
                    />
                  </div>
                </div>

                <div className="text-sm text-slate-500 mb-4">
                  <span>Coûts: {formatMoney(projet.total_achats_artisans)}</span>
                  <span className="mx-2">•</span>
                  <span>Budget client: {formatMoney(projet.budget_global_client)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                <Link
                  to={`/projets/${projet.id}`}
                  className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Ouvrir
                </Link>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/projets/${projet.id}/marge`}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                    title="Voir marge"
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => handleDelete(projet.id, projet.nom_complet)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjetsPage;
